import React, { useState, useEffect } from 'react';
import { Quote, Star, ChevronLeft, ChevronRight, Building2 } from 'lucide-react';
import './ModernTestimonials.scss';

const ModernTestimonials = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);

    const testimonials = [
        {
            quote: "Jaak Tech has been instrumental in modernizing our IT infrastructure. Their expertise in government procurement and dedication to customer success is unmatched. We've seen significant improvements in our operational efficiency.",
            author: "Sarah Johnson",
            title: "IT Director",
            company: "Federal Agency",
            rating: 5,
            image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=160&h=160&fit=crop&crop=face"
        },
        {
            quote: "Working with Jaak Tech simplified our technology procurement process significantly. Their team understands our compliance requirements and delivers every time. They've become an invaluable partner.",
            author: "Michael Chen",
            title: "CTO",
            company: "Healthcare Organization",
            rating: 5,
            image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=160&h=160&fit=crop&crop=face"
        },
        {
            quote: "The level of expertise and support we received from Jaak Tech exceeded our expectations. They've become our trusted partner for all technology needs. Their responsiveness is outstanding.",
            author: "David Martinez",
            title: "Operations Manager",
            company: "Defense Contractor",
            rating: 5,
            image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=160&h=160&fit=crop&crop=face"
        },
        {
            quote: "Jaak Tech's team provided exceptional service during our cloud migration. Their knowledge of enterprise solutions helped us achieve our goals on time and within budget.",
            author: "Emily Rodriguez",
            title: "VP of Technology",
            company: "Commercial Enterprise",
            rating: 5,
            image: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=160&h=160&fit=crop&crop=face"
        },
        {
            quote: "Outstanding technical expertise combined with excellent customer service. Jaak Tech delivered exactly what we needed for our digital transformation initiative.",
            author: "Robert Williams",
            title: "Program Manager",
            company: "State Government",
            rating: 5,
            image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=160&h=160&fit=crop&crop=face"
        },
        {
            quote: "From procurement to deployment, Jaak Tech made the entire process seamless. Their understanding of government regulations is exceptional and saved us valuable time.",
            author: "Jennifer Adams",
            title: "Deputy CIO",
            company: "Federal Department",
            rating: 5,
            image: "https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=160&h=160&fit=crop&crop=face"
        }
    ];

    // Auto-play carousel
    useEffect(() => {
        if (!isAutoPlaying) return;

        const interval = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % testimonials.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [isAutoPlaying, testimonials.length]);

    const nextTestimonial = () => {
        setIsAutoPlaying(false);
        setActiveIndex((prev) => (prev + 1) % testimonials.length);
    };

    const prevTestimonial = () => {
        setIsAutoPlaying(false);
        setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    };

    const goToSlide = (index) => {
        setIsAutoPlaying(false);
        setActiveIndex(index);
    };

    return (
        <section className="jk-testimonials">
            <div className="jk-testimonials__container">
                {/* Header */}
                <div className="jk-testimonials__header">
                    <span className="jk-section-label">
                        <Star size={16} />
                        Testimonials
                    </span>
                    <h2 className="jk-testimonials__title">
                        What Our Clients
                        <span className="jk-testimonials__title-highlight"> Say About Us</span>
                    </h2>
                    <p className="jk-testimonials__subtitle">
                        Trusted by government agencies, healthcare organizations, and enterprises across the nation.
                    </p>
                </div>

                {/* Carousel */}
                <div className="jk-testimonials__carousel">
                    <div className="jk-testimonials__viewport">
                        <div
                            className="jk-testimonials__track"
                            style={{
                                transform: `translateX(-${activeIndex * 100}%)`
                            }}
                        >
                            {testimonials.map((testimonial, index) => (
                                <div key={index} className="jk-testimonials__slide">
                                    <div className="jk-testimonials__card">
                                        <div className="jk-testimonials__card-inner">
                                            {/* Author Info - Left Side */}
                                            <div className="jk-testimonials__card-author">
                                                <img
                                                    className="jk-testimonials__card-avatar"
                                                    src={testimonial.image}
                                                    alt={testimonial.author}
                                                />
                                                <div className="jk-testimonials__card-info">
                                                    <span className="jk-testimonials__card-name">
                                                        {testimonial.author}
                                                    </span>
                                                    <span className="jk-testimonials__card-role">
                                                        {testimonial.title}
                                                    </span>
                                                    <span className="jk-testimonials__card-company">
                                                        <Building2 size={12} />
                                                        {testimonial.company}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Content - Right Side */}
                                            <div className="jk-testimonials__card-content">
                                                <div className="jk-testimonials__card-quote">
                                                    <Quote size={24} />
                                                </div>
                                                <div className="jk-testimonials__card-rating">
                                                    {[...Array(testimonial.rating)].map((_, i) => (
                                                        <Star key={i} size={16} fill="currentColor" />
                                                    ))}
                                                </div>
                                                <p className="jk-testimonials__card-text">
                                                    "{testimonial.quote}"
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Navigation */}
                <div className="jk-testimonials__nav">
                    <button
                        className="jk-testimonials__arrow"
                        onClick={prevTestimonial}
                        aria-label="Previous testimonial"
                    >
                        <ChevronLeft size={20} />
                    </button>

                    <div className="jk-testimonials__dots">
                        {testimonials.map((_, index) => (
                            <button
                                key={index}
                                className={`jk-testimonials__dot ${index === activeIndex ? 'is-active' : ''}`}
                                onClick={() => goToSlide(index)}
                                aria-label={`Go to testimonial ${index + 1}`}
                            />
                        ))}
                    </div>

                    <button
                        className="jk-testimonials__arrow"
                        onClick={nextTestimonial}
                        aria-label="Next testimonial"
                    >
                        <ChevronRight size={20} />
                    </button>
                </div>
            </div>
        </section>
    );
};

export default ModernTestimonials;
